/**
 * Admin User Detail
 * Comprehensive user profile, activity, organizations
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { adminApiGet, adminApiPatch, adminApiDelete } from '../../utils/adminApi';
import toast from '../../utils/toast';
import { BACKEND_URL } from '../../config';

import {
  ArrowLeft,
  ArrowSquareOut,
  Buildings,
  Calendar,
  CheckCircle,
  Clock,
  Coins,
  Copy,
  Envelope,
  GearSix,
  Gift,
  Prohibit,
  Pulse,
  Shield,
  Trash,
  User,
  WarningCircle
} from '@phosphor-icons/react';
const UserDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');
  const [isPrimaryUser, setIsPrimaryUser] = useState(false);

  useEffect(() => {
    fetchUserDetail();
  }, [userId]);

  const fetchUserDetail = async () => {
    try {
      setLoading(true);
      const data = await adminApiGet(`/api/admin/users/${userId}`);
      // Backend returns { user, organizations, is_primary_user, recent_activity }
      const userData = data.user || data;
      if (data.organizations) {
        userData.organizations = data.organizations;
      }
      setUser(userData);
      setIsPrimaryUser(data.is_primary_user || false);
    } catch (error) {
      console.error('Error fetching user:', error);
      toast.error('Failed to load user');
      navigate('/admin/users');
    } finally {
      setLoading(false);
    }
  };

  const handleSuspendUser = async () => {
    if (!window.confirm('Are you sure you want to suspend this user?')) return;
    
    try {
      await adminApiPatch(`/api/admin/users/${userId}/status`, {
        status: user.status === 'suspended' ? 'active' : 'suspended'
      });
      toast.success(`User ${user.status === 'suspended' ? 'activated' : 'suspended'}`);
      fetchUserDetail();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update user status');
    }
  };

  const handleResetPassword = async () => {
    if (!window.confirm(`Send a password reset email to ${user.email}?`)) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email })
      });
      
      if (response.ok) {
        toast.success(`Password reset email sent to ${user.email}`);
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to send reset email');
      }
    } catch (error) {
      console.error('Error sending reset email:', error);
      toast.error('Failed to send password reset email');
    }
  };

  const handleDeleteUser = async () => {
    if (!window.confirm(`Are you sure you want to DELETE user ${user.email}? This action cannot be undone!`)) return;
    if (!window.confirm(`FINAL WARNING: This will permanently delete the account for ${user.email}. Continue?`)) return;
    
    try {
      await adminApiDelete(`/api/admin/users/${encodeURIComponent(user.email)}`);
      toast.success('User account deleted successfully');
      navigate('/admin/users');
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.message || 'Failed to delete user account');
    }
  };

  // Dynamically build tabs based on user type
  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    ...(isPrimaryUser ? [{ id: 'plan', label: 'Plan & Limits', icon: Shield }] : []),
    { id: 'credits', label: 'Credits', icon: Coins },
    { id: 'organizations', label: 'Organizations', icon: Buildings },
    { id: 'activity', label: 'Activity', icon: Pulse },
    { id: 'settings', label: 'Settings', icon: GearSix },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-32 bg-white/10 rounded-xl animate-pulse"></div>
        <div className="h-96 bg-white/10 rounded-xl animate-pulse"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-12">
        <WarningCircle size={48} className="text-gray-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-white mb-2">User Not Found</h3>
        <button onClick={() => navigate('/admin/users')} className="text-[#6366F1] hover:underline">
          Back to Users
        </button>
      </div>
    );
  }

  const getPlanBadge = (plan) => {
    const colors = {
      basic: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      pro: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'partner-pro': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      partner_pro: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    };
    return colors[plan?.toLowerCase()] || colors.basic;
  };

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button
        onClick={() => navigate('/admin/users')}
        className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
      >
        <ArrowLeft size={20} />
        <span>Back to Users</span>
      </button>

      {/* Header */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#6366F1] to-[#EC4899] flex items-center justify-center text-white font-bold text-2xl">
              {user.name?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white mb-2">{user.name}</h1>
              <div className="flex items-center gap-4 mb-2">
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Envelope size={14} />
                  {user.email}
                </div>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(user.id);
                    toast.success('Copied', 'User ID copied to clipboard');
                  }}
                  className="flex items-center gap-1 text-sm text-gray-400 hover:text-white"
                >
                  <Copy size={14} />
                  ID: {user.id?.substring(0, 8)}...
                </button>
              </div>
              <div className="flex items-center gap-2">
                {/* User Type Badge - Primary vs Member */}
                <span className={`px-3 py-1 text-xs font-medium rounded-full border ${
                  isPrimaryUser
                    ? 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30'
                    : 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                }`}>
                  {isPrimaryUser ? '👑 Primary User' : '👤 Member User'}
                </span>
                <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getPlanBadge(user.plan)}`}>
                  {user.plan || 'basic'}
                </span>
                <span 
                  className={`px-3 py-1 text-xs font-medium rounded-full border flex items-center gap-1 ${
                    (user.status || 'active') === 'active'
                      ? 'bg-green-500/20 text-green-400 border-green-500/30'
                      : 'bg-red-500/20 text-red-400 border-red-500/30'
                  }`}
                  title={`Account Status: ${(user.status || 'active') === 'active' ? 'Account is active and can log in' : 'Account is suspended and cannot log in'}`}
                >
                  {(user.status || 'active') === 'active' ? <CheckCircle size={12} /> : <Prohibit size={12} />}
                  {user.status || 'active'}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleSuspendUser}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                user.status === 'suspended'
                  ? 'bg-green-500/20 border border-green-500/30 text-green-400 hover:bg-green-500/30'
                  : 'bg-red-500/20 border border-red-500/30 text-red-400 hover:bg-red-500/30'
              }`}
            >
              {user.status === 'suspended' ? <CheckCircle size={16} /> : <Prohibit size={16} />}
              <span className="text-sm">{user.status === 'suspended' ? 'Activate' : 'Suspend'}</span>
            </button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-4 gap-4 pt-6 border-t border-white/10">
          <div>
            <p className="text-xs text-gray-500 mb-1">Organizations</p>
            <p className="text-xl font-bold text-white">{user.organizations?.length || 0}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Joined</p>
            <p className="text-xl font-bold text-white">
              {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Last Login</p>
            <p className="text-xl font-bold text-white">
              {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Auth Provider</p>
            <p className="text-xl font-bold text-white capitalize">{user.auth_provider || 'email'}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden">
        <div className="flex border-b border-white/10 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-white/5 text-[#6366F1] border-b-2 border-[#6366F1]'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'profile' && <ProfileTab user={user} isPrimaryUser={isPrimaryUser} />}
          {activeTab === 'plan' && isPrimaryUser && <PlanTab user={user} onUpdate={fetchUserDetail} />}
          {activeTab === 'credits' && <CreditsTab userId={userId} user={user} />}
          {activeTab === 'organizations' && <OrganizationsTab user={user} navigate={navigate} />}
          {activeTab === 'activity' && <ActivityTab userId={userId} />}
          {activeTab === 'settings' && <SettingsTab user={user} onUpdate={fetchUserDetail} onResetPassword={handleResetPassword} onDeleteUser={handleDeleteUser} />}
        </div>
      </div>
    </div>
  );
};

// Profile Tab Component
const ProfileTab = ({ user, isPrimaryUser }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-2 gap-6">
      <div>
        <h3 className="text-sm font-semibold text-gray-400 mb-3">User Information</h3>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-400">Name</span>
            <span className="text-white font-medium">{user.name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Email</span>
            <span className="text-white font-medium">{user.email}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Email Verified</span>
            <span className="text-white font-medium">
              {user.email_verified ? '✓ Yes' : '✗ No'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">User Type</span>
            <span className={`font-medium ${isPrimaryUser ? 'text-indigo-400' : 'text-amber-400'}`}>
              {isPrimaryUser ? '👑 Primary User (Org Owner)' : '👤 Member User'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Plan</span>
            <span className="text-white font-medium">{user.plan || 'basic'}</span>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-400 mb-3">Account Details</h3>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-400">Status</span>
            <span className="text-white font-medium">{user.status || 'active'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Auth Provider</span>
            <span className="text-white font-medium capitalize">{user.auth_provider || 'email'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Created</span>
            <span className="text-white font-medium">
              {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
            </span>
          </div>
        </div>
      </div>
    </div>
    
    {/* Member User Info Box */}
    {!isPrimaryUser && (
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
        <h4 className="text-amber-400 font-medium mb-2">📝 Member User</h4>
        <p className="text-sm text-gray-300">
          This user is a member (invited) user and does not own any organizations. 
          Their plan and limits are inherited from the organization(s) they belong to. 
          Plan changes are only available for Primary Users who own organizations.
        </p>
      </div>
    )}
  </div>
);

// Organizations Tab Component - Shows which orgs the user belongs to
const OrganizationsTab = ({ user, navigate }) => {
  return (
    <div className="space-y-4">
      {user.organizations && user.organizations.length > 0 ? (
        <>
          <div className="text-sm text-gray-400 mb-4">
            This user is associated with {user.organizations.length} organization{user.organizations.length !== 1 ? 's' : ''}
          </div>
          {user.organizations.map((org) => (
            <div key={org.organization_id} className="bg-white/5 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#6366F1] to-[#EC4899] flex items-center justify-center">
                    <Buildings size={20} className="text-white" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">{org.name || 'Unnamed Organization'}</h4>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        org.role === 'owner'
                          ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30'
                          : org.role === 'admin'
                          ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                          : 'bg-gray-500/20 text-gray-300 border border-gray-500/30'
                      }`}>
                        {org.role === 'owner' ? '👑 Owner' : org.role === 'admin' ? '⚡ Admin' : '👤 Member'}
                      </span>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        org.tier === 'partner_pro' || org.tier === 'partner-pro'
                          ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                          : org.tier === 'pro'
                          ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                          : 'bg-gray-500/20 text-gray-300 border border-gray-500/30'
                      }`}>
                        {org.tier || 'basic'}
                      </span>
                      {org.is_primary && (
                        <span className="px-2 py-0.5 text-xs bg-green-500/20 text-green-300 border border-green-500/30 rounded-full">
                          Primary Org
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/admin/organizations/${org.organization_id}`)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  title="View organization details"
                >
                  <ArrowSquareOut size={16} className="text-gray-400" />
                </button>
              </div>
            </div>
          ))}
        </>
      ) : (
        <div className="text-center py-8">
          <Buildings size={48} className="text-gray-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">No Organizations</h3>
          <p className="text-gray-400">This user is not associated with any organizations yet</p>
        </div>
      )}
    </div>
  );
};

// Pulse Tab Component
const ActivityTab = ({ userId }) => (
  <div className="space-y-4">
    <div className="text-center py-8">
      <Pulse size={48} className="text-gray-500 mx-auto mb-4" />
      <h3 className="text-lg font-semibold text-white mb-2">Pulse Log</h3>
      <p className="text-gray-400">User activity tracking coming soon</p>
    </div>
  </div>
);

// GearSix Tab Component
const SettingsTab = ({ user, onUpdate, onResetPassword, onDeleteUser }) => (
  <div className="space-y-6">
    <div className="bg-white/5 rounded-lg p-6">
      <h3 className="text-lg font-semibold text-white mb-4">Danger Zone</h3>
      <div className="space-y-3">
        <button 
          onClick={onResetPassword}
          className="w-full px-4 py-2 bg-yellow-500/20 border border-yellow-500/30 text-yellow-400 rounded-lg hover:bg-yellow-500/30 transition-colors text-left"
          data-testid="admin-reset-password-btn"
        >
          Reset Password
        </button>
        <button 
          onClick={onDeleteUser}
          className="w-full px-4 py-2 bg-red-500/20 border border-red-500/30 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors text-left"
          data-testid="admin-delete-user-btn"
        >
          Delete User Account
        </button>
      </div>
    </div>
  </div>
);

// Plan & Limits Tab Component (PRIMARY CONTROL - Single Source of Truth)
const PlanTab = ({ user, onUpdate }) => {
  const [selectedPlan, setSelectedPlan] = useState(user?.plan || 'basic');
  const [saving, setSaving] = useState(false);

  const plans = [
    {
      id: 'basic',
      name: 'Basic',
      description: 'View-only assessment platform',
      color: 'gray',
      limits: {
        max_agents: 0,
        max_voice_agents: 0,
        multi_tenant: false
      },
      features: [
        'Limited assessments',
        'View 1 opportunity',
        'No agents or voice',
        'No multi-tenant'
      ]
    },
    {
      id: 'pro',
      name: 'Pro',
      description: 'Full AI Workforce capabilities',
      color: 'blue',
      limits: {
        max_agents: 3,
        max_voice_agents: 1,
        multi_tenant: false
      },
      features: [
        'Up to 3 AI agents',
        'Up to 1 voice agent',
        'Advanced analytics',
        'Team access (5 users)',
        'Full assessments'
      ]
    },
    {
      id: 'partner_pro',
      name: 'Partner Pro',
      description: 'Enterprise & consultant tier',
      color: 'purple',
      limits: {
        max_agents: '∞',
        max_voice_agents: '∞',
        multi_tenant: true
      },
      features: [
        'Unlimited AI agents',
        'Unlimited voice agents',
        'Multi-tenant enabled',
        'Consultant features',
        'White-label options',
        'Priority support'
      ]
    }
  ];

  const currentPlan = plans.find(p => p.id === (user?.plan || 'basic'));

  const handleSavePlan = async () => {
    if (selectedPlan === user?.plan) {
      toast.info('No changes to save');
      return;
    }

    try {
      setSaving(true);
      await adminApiPatch(`/api/admin/users/${user.id}/plan`, {
        plan: selectedPlan
      });
      toast.success(`Plan updated to ${plans.find(p => p.id === selectedPlan)?.name}`);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error updating plan:', error);
      toast.error(error.message || 'Failed to update plan');
      setSelectedPlan(user?.plan || 'basic');
    } finally {
      setSaving(false);
    }
  };

  const getPlanCardClass = (planId, isSelected) => {
    const baseClass = 'relative p-6 rounded-lg border-2 transition-all cursor-pointer ';
    if (isSelected) {
      const colors = {
        gray: 'bg-gray-500/10 border-gray-400 shadow-lg shadow-gray-500/20',
        blue: 'bg-blue-500/10 border-blue-400 shadow-lg shadow-blue-500/20',
        purple: 'bg-purple-500/10 border-purple-400 shadow-lg shadow-purple-500/20'
      };
      return baseClass + (colors[plans.find(p => p.id === planId)?.color] || colors.gray);
    }
    return baseClass + 'bg-white/5 border-white/20 hover:border-white/40';
  };

  return (
    <div className="space-y-6">
      {/* Current Plan Banner */}
      <div className="bg-gradient-to-r from-[#6366F1]/20 to-[#EC4899]/20 border border-[#6366F1]/30 rounded-lg p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Shield size={24} className="text-[#6366F1]" />
              <h3 className="text-xl font-bold text-white">Current Plan: {currentPlan?.name}</h3>
            </div>
            <p className="text-gray-300 mb-4">{currentPlan?.description}</p>
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-gray-400">Agents:</span>
                <span className="text-white font-medium">{currentPlan?.limits.max_agents}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-400">Voice:</span>
                <span className="text-white font-medium">{currentPlan?.limits.max_voice_agents}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-400">Multi-Tenant:</span>
                <span className={`font-medium ${currentPlan?.limits.multi_tenant ? 'text-green-400' : 'text-gray-500'}`}>
                  {currentPlan?.limits.multi_tenant ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Plan Selection */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">Change Plan</h3>
        <div className="grid grid-cols-3 gap-4">
          {plans.map((plan) => (
            <div
              key={plan.id}
              onClick={() => setSelectedPlan(plan.id)}
              className={getPlanCardClass(plan.id, selectedPlan === plan.id)}
            >
              {selectedPlan === plan.id && (
                <div className="absolute top-3 right-3">
                  <CheckCircle size={24} className="text-green-400" />
                </div>
              )}
              
              <h4 className="text-xl font-bold text-white mb-2">{plan.name}</h4>
              <p className="text-sm text-gray-400 mb-4">{plan.description}</p>
              
              <div className="space-y-2 mb-4">
                <div className="text-sm">
                  <span className="text-gray-400">Agents: </span>
                  <span className="text-white font-medium">{plan.limits.max_agents}</span>
                </div>
                <div className="text-sm">
                  <span className="text-gray-400">Voice: </span>
                  <span className="text-white font-medium">{plan.limits.max_voice_agents}</span>
                </div>
                <div className="text-sm">
                  <span className="text-gray-400">Multi-Tenant: </span>
                  <span className={`font-medium ${plan.limits.multi_tenant ? 'text-green-400' : 'text-gray-500'}`}>
                    {plan.limits.multi_tenant ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>

              <div className="pt-4 border-t border-white/10">
                <ul className="space-y-1">
                  {plan.features.map((feature, idx) => (
                    <li key={`feature-${feature.slice(0,20)}-${idx}`} className="text-xs text-gray-400 flex items-start gap-2">
                      <CheckCircle size={12} className="text-green-400 mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Save Button */}
      {selectedPlan !== user?.plan && (
        <div className="flex items-center justify-between p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
          <div className="flex items-center gap-3">
            <WarningCircle size={20} className="text-blue-400" />
            <div>
              <p className="text-white font-medium">Plan Change Pending</p>
              <p className="text-sm text-gray-400">
                {user?.plan || 'basic'} → {selectedPlan}
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setSelectedPlan(user?.plan || 'basic')}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors text-white"
            >
              Cancel
            </button>
            <button
              onClick={handleSavePlan}
              disabled={saving}
              className="px-6 py-2 bg-[#6366F1] hover:bg-[#5558E3] rounded-lg transition-colors text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-white/5 rounded-lg p-4">
        <h4 className="text-white font-medium mb-2">📝 Plan Management Notes</h4>
        <ul className="text-sm text-gray-400 space-y-1">
          <li>• Plans are the <strong className="text-white">single source of truth</strong> for all feature limits</li>
          <li>• All organizations owned by this user inherit these capabilities</li>
          <li>• Changes take effect immediately across the platform</li>
          <li>• Agent/voice limits are enforced at creation time</li>
        </ul>
      </div>
    </div>
  );
};

// Credits Tab Component
const CreditsTab = ({ userId, user }) => {
  const [creditData, setCreditData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addAmount, setAddAmount] = useState('');
  const [addReason, setAddReason] = useState('');
  const [adding, setAdding] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [txPage, setTxPage] = useState(1);

  const fetchCredits = async () => {
    setLoading(true);
    try {
      const data = await adminApiGet(`/api/admin/users/${userId}/credits`);
      setCreditData(data);
    } catch (err) {
      console.error('Failed to fetch credits:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCredits(); }, [userId]);

  const handleAddCredits = async () => {
    const amount = parseInt(addAmount, 10);
    if (!amount || amount <= 0) return;
    setAdding(true);
    try {
      await adminApiPost(`/api/admin/users/${userId}/credits/add`, {
        amount,
        reason: addReason || 'Admin reward',
      });
      setAddAmount('');
      setAddReason('');
      await fetchCredits();
    } catch (err) {
      console.error('Failed to add credits:', err);
    } finally {
      setAdding(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-6 h-6 border-2 border-[#6366F1] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!creditData) {
    return <div className="text-gray-400 py-8 text-center">Failed to load credit data</div>;
  }

  const { balance, monthly_allocation, plan, stats, breakdown, transactions } = creditData;
  const usagePercent = monthly_allocation > 0 ? Math.min(100, ((stats?.monthly_used || 0) / monthly_allocation) * 100) : 0;

  const reasonLabels = {
    command_complex: 'Complex Commands',
    command_standard: 'Standard Commands',
    command_simple: 'Simple Commands',
    agent_creation: 'Agent Creation',
    agent_run: 'Agent Execution',
    tts_synthesis: 'Text-to-Speech',
    process_analysis: 'Process Analysis',
    admin_credit_addition: 'Admin Reward',
    monthly_reset: 'Monthly Reset',
    topup_purchase: 'Purchased Top-up',
    credit_topup: 'Credit Top-up',
    stripe_topup: 'Stripe Top-up',
  };

  const filteredTxs = transactions?.filter(tx => {
    if (filterType === 'all') return true;
    if (filterType === 'deductions') return tx.amount < 0;
    if (filterType === 'topups') return tx.amount > 0;
    if (filterType === 'admin') return tx.reason === 'admin_credit_addition';
    return true;
  }) || [];

  return (
    <div className="space-y-6" data-testid="credits-tab">
      {/* Credit Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white/5 rounded-lg p-4 border border-white/10">
          <div className="text-xs text-gray-400 mb-1">Current Balance</div>
          <div className="text-2xl font-bold text-white" data-testid="credit-balance">{balance?.toLocaleString() || 0}</div>
          <div className="text-xs text-gray-500 mt-1">{plan} plan</div>
        </div>
        <div className="bg-white/5 rounded-lg p-4 border border-white/10">
          <div className="text-xs text-gray-400 mb-1">Monthly Allocation</div>
          <div className="text-2xl font-bold text-white">{monthly_allocation?.toLocaleString()}</div>
          <div className="text-xs text-gray-500 mt-1">{usagePercent.toFixed(0)}% used this month</div>
        </div>
        <div className="bg-white/5 rounded-lg p-4 border border-white/10">
          <div className="text-xs text-gray-400 mb-1">Admin Rewards</div>
          <div className="text-2xl font-bold text-emerald-400">{stats?.admin_credits_given?.toLocaleString() || 0}</div>
          <div className="text-xs text-gray-500 mt-1">{stats?.admin_credits_count || 0} additions</div>
        </div>
        <div className="bg-white/5 rounded-lg p-4 border border-white/10">
          <div className="text-xs text-gray-400 mb-1">Purchased Top-ups</div>
          <div className="text-2xl font-bold text-blue-400">{stats?.purchased_credits?.toLocaleString() || 0}</div>
          <div className="text-xs text-gray-500 mt-1">{stats?.purchased_count || 0} purchases</div>
        </div>
      </div>

      {/* Usage Progress Bar */}
      <div className="bg-white/5 rounded-lg p-4 border border-white/10">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-gray-400">Monthly Usage</span>
          <span className="text-sm text-white font-medium">{stats?.monthly_used?.toLocaleString() || 0} / {monthly_allocation?.toLocaleString()} credits</span>
        </div>
        <div className="w-full bg-white/10 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all ${usagePercent > 80 ? 'bg-red-500' : usagePercent > 50 ? 'bg-amber-500' : 'bg-emerald-500'}`}
            style={{ width: `${usagePercent}%` }}
          />
        </div>
        <div className="flex justify-between mt-2 text-xs text-gray-500">
          <span>{stats?.monthly_transactions || 0} transactions this month</span>
          <span>{stats?.weekly_used?.toLocaleString() || 0} used this week</span>
        </div>
      </div>

      {/* Add Credits (Admin Reward) */}
      <div className="bg-gradient-to-r from-[#6366F1]/10 to-emerald-500/10 rounded-lg p-4 border border-[#6366F1]/30">
        <div className="flex items-center gap-2 mb-3">
          <Gift size={18} className="text-emerald-400" />
          <h3 className="text-sm font-semibold text-white">Add Free Credits</h3>
        </div>
        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <label className="text-xs text-gray-400 block mb-1">Amount</label>
            <input
              type="number"
              value={addAmount}
              onChange={(e) => setAddAmount(e.target.value)}
              placeholder="e.g. 500"
              min="1"
              max="50000"
              className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#6366F1]"
              data-testid="credit-add-amount"
            />
          </div>
          <div className="flex-[2]">
            <label className="text-xs text-gray-400 block mb-1">Reason</label>
            <input
              type="text"
              value={addReason}
              onChange={(e) => setAddReason(e.target.value)}
              placeholder="e.g. Early adopter reward"
              className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#6366F1]"
              data-testid="credit-add-reason"
            />
          </div>
          <button
            onClick={handleAddCredits}
            disabled={adding || !addAmount || parseInt(addAmount) <= 0}
            className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-white text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
            data-testid="credit-add-btn"
          >
            {adding ? 'Adding...' : 'Add Credits'}
          </button>
        </div>
      </div>

      {/* Usage Breakdown */}
      {breakdown && breakdown.length > 0 && (
        <div className="bg-white/5 rounded-lg p-4 border border-white/10">
          <h3 className="text-sm font-semibold text-gray-400 mb-3">Usage Breakdown (This Month)</h3>
          <div className="space-y-2">
            {breakdown.map((b, i) => {
              const maxTotal = breakdown[0]?.total || 1;
              const barWidth = (b.total / maxTotal) * 100;
              return (
                <div key={`breakdown-${b.reason}-${i}`} className="flex items-center gap-3">
                  <div className="w-36 text-xs text-gray-400 truncate">{reasonLabels[b.reason] || b.reason}</div>
                  <div className="flex-1 bg-white/5 rounded-full h-2">
                    <div className="bg-[#6366F1] h-2 rounded-full" style={{ width: `${barWidth}%` }} />
                  </div>
                  <div className="w-20 text-right text-xs text-white font-medium">{b.total} ({b.count}x)</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Transaction History */}
      <div className="bg-white/5 rounded-lg border border-white/10">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h3 className="text-sm font-semibold text-gray-400">Transaction History</h3>
          <div className="flex gap-1">
            {['all', 'deductions', 'topups', 'admin'].map(f => (
              <button
                key={f}
                onClick={() => setFilterType(f)}
                className={`px-3 py-1 rounded-md text-xs transition-colors ${
                  filterType === f
                    ? 'bg-[#6366F1] text-white'
                    : 'bg-white/5 text-gray-400 hover:bg-white/10'
                }`}
                data-testid={`credit-filter-${f}`}
              >
                {f === 'all' ? 'All' : f === 'deductions' ? 'Usage' : f === 'topups' ? 'Top-ups' : 'Admin'}
              </button>
            ))}
          </div>
        </div>
        <div className="max-h-[400px] overflow-y-auto">
          {filteredTxs.length === 0 ? (
            <div className="p-8 text-center text-gray-500 text-sm">No transactions found</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-white/5 sticky top-0">
                <tr className="text-gray-400 text-xs">
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">Type</th>
                  <th className="text-right p-3">Amount</th>
                  <th className="text-right p-3">Balance After</th>
                  <th className="text-left p-3">Details</th>
                </tr>
              </thead>
              <tbody>
                {filteredTxs.map((tx, i) => (
                  <tr key={tx.id || tx.tx_id || tx.transaction_id || `${tx.created_at || ''}-${tx.reason || ''}-${i}`} className="border-t border-white/5 hover:bg-white/5">
                    <td className="p-3 text-gray-400 text-xs">
                      {tx.created_at ? new Date(tx.created_at).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}
                    </td>
                    <td className="p-3">
                      <span className="text-xs text-gray-300">{reasonLabels[tx.reason] || tx.reason}</span>
                    </td>
                    <td className={`p-3 text-right font-medium ${tx.amount > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {tx.amount > 0 ? '+' : ''}{tx.amount}
                    </td>
                    <td className="p-3 text-right text-gray-300">{tx.balance_after?.toLocaleString()}</td>
                    <td className="p-3 text-xs text-gray-500">
                      {tx.metadata?.admin_note || tx.metadata?.description || ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDetail;
